// Render a SegmentGraph as a chain of truncated cones.
//
// v0 expects degenerate-bezier segments (straight axes), so each
// Segment maps to one Three.js cylinder. When a future generator
// emits non-degenerate beziers, this component will need to
// subdivide the curve into N truncated cones — that's the
// "decompose" pass discussed in the framing doc / types.ts comment.
//
// Cylinder convention: Three.js cylinderGeometry has its axis
// along Y, with the larger radius at the bottom (-Y) by convention
// when args are (radiusTop, radiusBottom, height). We orient the
// cylinder so its +Y end aligns with axis.p3 and -Y end aligns with
// axis.p0 — which means radiusTop = radiusProfile.tip (at p3) and
// radiusBottom = radiusProfile.base (at p0).

import { useMemo } from 'react';
import { Quaternion, Vector3 } from 'three';
import type { SegmentGraph, Segment, V3 } from 'heartwood-tree-gen';
import { palette } from '../../theme';

const UP = new Vector3(0, 1, 0);
const RADIAL_SEGMENTS = 12;

interface TreeMeshProps {
  graph: SegmentGraph;
  /** Optional override for the bark material color. Defaults to
   *  theme palette. */
  color?: string;
}

export function TreeMesh({ graph, color }: TreeMeshProps): React.ReactElement {
  const barkColor = color ?? palette.barkBrown;
  return (
    <group>
      {graph.segments.map(segment => (
        <SegmentMesh key={segment.id} segment={segment} color={barkColor} />
      ))}
    </group>
  );
}

interface SegmentMeshProps {
  segment: Segment;
  color: string;
}

function SegmentMesh({ segment, color }: SegmentMeshProps): React.ReactElement | null {
  const transform = useMemo(() => buildTransform(segment), [segment]);
  if (transform === null) return null; // zero-length / degenerate segment

  const radiusTop = Math.max(segment.radiusProfile.tip, 1e-4);
  const radiusBottom = Math.max(segment.radiusProfile.base, 1e-4);

  return (
    <mesh
      position={transform.position}
      quaternion={transform.quaternion}
      castShadow
      receiveShadow
    >
      <cylinderGeometry args={[radiusTop, radiusBottom, transform.length, RADIAL_SEGMENTS]} />
      <meshStandardMaterial color={color} roughness={0.85} />
    </mesh>
  );
}

interface SegmentTransform {
  position: [number, number, number];
  quaternion: Quaternion;
  length: number;
}

function buildTransform(segment: Segment): SegmentTransform | null {
  const p0 = toVec(segment.axis.p0);
  const p3 = toVec(segment.axis.p3);
  const direction = new Vector3().subVectors(p3, p0);
  const length = direction.length();
  if (length < 1e-6) return null;

  // The cylinder is centered at the midpoint of the segment in
  // world space. Rotate from the canonical +Y axis to the
  // segment's direction.
  const midpoint = new Vector3().addVectors(p0, p3).multiplyScalar(0.5);
  direction.normalize();
  const quaternion = new Quaternion().setFromUnitVectors(UP, direction);

  return {
    position: [midpoint.x, midpoint.y, midpoint.z],
    quaternion,
    length,
  };
}

function toVec(v: V3): Vector3 {
  return new Vector3(v[0], v[1], v[2]);
}
